import axios from 'axios';

// Interface definitions
export interface Datatable {
    id: string;
    name: string;
    orgId?: string;
    mongoCollectionName: string;
}

export interface DocumentSummary {
    documentList: Record<string, any>[];
    noOfRecords: number;
    totalRecords: number;
}

// Get auth token from localStorage
const getAuthToken = (): string => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create API instance
const designerApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/designer/api' : '/designer/api',
});

// Add request interceptor for auth token
designerApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Get all datatables
export const getDatatables = async (): Promise<Datatable[]> => {
    const response = await designerApi.get<Datatable[]>('/datatable/');
    return response.data;
};

// Create a new datatable
export const createDatatable = async (data: Partial<Datatable>): Promise<Datatable> => {
    const response = await designerApi.post<Datatable>('/datatable/', data);
    return response.data;
};

// Delete a datatable by ID
export const deleteDatatable = async (id: string): Promise<void> => {
    await designerApi.delete(`/datatable/${id}`);
};

// Get paginated data from a collection
export const getTableData = async (
    collectionName: string,
    page: number = 1,
    size: number = 10,
    sortBy: string = '_id',
    sortOrder: string = 'DESC'
): Promise<DocumentSummary> => {
    const response = await designerApi.post<DocumentSummary>(
        `/datatable/getData/${collectionName}`,
        { page, size, sortBy, sortOrder }
    );
    return response.data;
};

// Get all data from a collection (no pagination)
export const getAllTableData = async (collectionName: string): Promise<Record<string, any>[]> => {
    const response = await designerApi.post<Record<string, any>[]>(`/datatable/getAll/${collectionName}`);
    return response.data;
};

// Insert a new record into a collection
export const insertRecord = async (
    collectionName: string,
    document: Record<string, any>,
    idPrefix?: string
): Promise<Record<string, any>> => {
    const params: Record<string, string> = { collectionName };
    if (idPrefix) {
        params.idPrefix = idPrefix;
    }
    const response = await designerApi.post<Record<string, any>>(
        '/datatable/insertData',
        document,
        { params }
    );
    return response.data;
};

// Update a record by ID
export const updateRecord = async (
    collectionName: string,
    id: string,
    document: Record<string, any>
): Promise<void> => {
    await designerApi.post('/datatable/updateData', document, {
        params: { collectionName, id }
    });
};

// Delete a record by ID
export const deleteRecord = async (collectionName: string, id: string): Promise<void> => {
    await designerApi.delete('/datatable/deleteDataById', {
        params: { collectionName, id }
    });
};

// Get a record by ID
export const getRecordById = async (
    collectionName: string,
    id: string
): Promise<Record<string, any>> => {
    const response = await designerApi.get<Record<string, any>>(
        `/datatable/getDataById/${collectionName}/${id}`
    );
    return response.data;
};

// Export default object
export default {
    getDatatables,
    createDatatable,
    deleteDatatable,
    getTableData,
    getAllTableData,
    insertRecord,
    updateRecord,
    deleteRecord,
    getRecordById
};
